import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { programService, Program, ProgramBeneficiary } from '../../../firebase/programFirestoreService';
import { useAuth } from '../../../contexts/AuthContext';
import { useUI } from '../../../components/common/UIMessageProvider';
import { searchBeneficiaryByGhanaCard } from '../../../firebase/beneficiaryFirestoreService';
import debounce from 'lodash/debounce';
import { useLocation, useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

interface FormData {
  programId: string;
  fullName: string;
  idNumber?: string;
  idType?: string;
  gender: 'male' | 'female' | 'other' | '';
  religion?: 'christian' | 'muslim' | 'other' | '';
  contactNumber: string;
  address?: string;
  email?: string;
  maritalStatus?: string;
  healthStatus?: string;
  dob?: string;
  agreeToTerms?: boolean;
}

interface SearchResult {
  beneficiary: ProgramBeneficiary;
  programTitle: string;
  registrationDate: string;
}

interface EditRegistrationPayload {
  id?: string;
  beneficiaryId?: string;
  programId?: string;
  programTitle?: string;
  fullName?: string;
  idNumber?: string;
  idType?: string;
  gender?: string;
  religion?: string;
  contactNumber?: string;
  houseNumber?: string;
  address?: string;
  email?: string;
  maritalStatus?: string;
  healthStatus?: string;
  status?: string;
  dateOfBirth?: unknown;
}

interface AdminRegistrationLocationState {
  editRegistration?: EditRegistrationPayload;
}

const EDIT_REGISTRATION_STORAGE_PREFIX = 'admin-program-registration-edit';

const AdminProgramRegistrationForm: React.FC = () => {
  const { currentUser } = useAuth();
  const ui = useUI();
  const location = useLocation();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<ProgramBeneficiary | null>(null);
  const [hasManualEdits, setHasManualEdits] = useState(false);
  // NEW: Store original values to compare for changes
  const [originalValues, setOriginalValues] = useState<Partial<FormData>>({});
  const locationStateEditRegistration = ((location.state as AdminRegistrationLocationState | null)?.editRegistration ?? null);
  const [cachedEditRegistration, setCachedEditRegistration] = useState<EditRegistrationPayload | null>(null);
  const editRegistration = locationStateEditRegistration ?? cachedEditRegistration;
  const isEditMode = !!editRegistration;

  const { register, handleSubmit, setValue, setError, clearErrors, formState: { errors }, reset, watch } = useForm<FormData>();

  const programId = watch('programId');
  const selectedProgram = programs.find((program) => program.id === programId);
  const requiresDateOfBirth = !!selectedProgram?.requireDateOfBirth;
  const normalizeReligion = (value?: string): FormData['religion'] => {
    const raw = (value || '').toString().trim().toLowerCase();
    if (raw === 'christian' || raw === 'muslim' || raw === 'other') return raw;
    return '';
  };
  const formatDateForInput = (value?: unknown): string => {
    if (!value) return '';

    if (typeof value === 'string') {
      const cleaned = value.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) return cleaned;
      const parsed = new Date(cleaned);
      if (!Number.isNaN(parsed.getTime())) {
        const year = parsed.getFullYear();
        const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
        const day = `${parsed.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return '';
    }

    const candidate = typeof (value as any)?.toDate === 'function'
      ? (value as any).toDate()
      : value instanceof Date
        ? value
        : null;

    if (!candidate || Number.isNaN(candidate.getTime())) return '';
    const year = candidate.getFullYear();
    const month = `${candidate.getMonth() + 1}`.padStart(2, '0');
    const day = `${candidate.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    loadPrograms();
  }, []);

  useEffect(() => {
    if (locationStateEditRegistration) {
      setCachedEditRegistration(null);
      return;
    }

    const params = new URLSearchParams(location.search);
    const editToken = params.get('editToken');

    if (!editToken) {
      setCachedEditRegistration(null);
      return;
    }

    try {
      const storageKey = `${EDIT_REGISTRATION_STORAGE_PREFIX}:${editToken}`;
      const payload = localStorage.getItem(storageKey);
      if (!payload) {
        setCachedEditRegistration(null);
        return;
      }

      const parsedPayload = JSON.parse(payload) as EditRegistrationPayload;
      setCachedEditRegistration(parsedPayload);
    } catch (error) {
      console.error('Error restoring edit registration payload:', error);
      setCachedEditRegistration(null);
    }
  }, [location.search, locationStateEditRegistration]);

  useEffect(() => {
    if (!editRegistration) return;

    const incomingGender = (editRegistration.gender || '').toLowerCase();
    const normalizedGender: FormData['gender'] =
      incomingGender === 'male' || incomingGender === 'female'
        ? (incomingGender as FormData['gender'])
        : '';

    const prefilled: Partial<FormData> = {
      programId: editRegistration.programId || '',
      fullName: editRegistration.fullName || '',
      idNumber: editRegistration.idNumber || '',
      gender: normalizedGender,
      religion: normalizeReligion(editRegistration.religion),
      contactNumber: editRegistration.contactNumber || '',
      address: editRegistration.address || '',
      email: editRegistration.email || '',
      maritalStatus: editRegistration.maritalStatus || '',
      healthStatus: editRegistration.healthStatus || '',
      dob: formatDateForInput(editRegistration.dateOfBirth)
    };

    (Object.entries(prefilled) as Array<[keyof FormData, FormData[keyof FormData]]>).forEach(([field, value]) => {
      setValue(field, value as any, { shouldDirty: false, shouldValidate: false });
    });

    setOriginalValues(prefilled);
    setSelectedBeneficiary({
      id: editRegistration.id,
      beneficiaryId: editRegistration.beneficiaryId || '',
      fullName: editRegistration.fullName || '',
      gender: (normalizedGender || 'male') as 'male' | 'female',
      contactNumber: editRegistration.contactNumber || '',
      houseNumber: editRegistration.houseNumber || '',
      address: editRegistration.address || '',
      email: editRegistration.email || '',
      idNumber: editRegistration.idNumber || '',
      idType: editRegistration.idType || 'ghana-card',
      religion: editRegistration.religion || '',
      maritalStatus: editRegistration.maritalStatus || '',
      healthStatus: editRegistration.healthStatus || '',
      dateOfBirth: editRegistration.dateOfBirth as any,
      registrationStatus: (editRegistration.status as any) || 'confirmed',
      registrationNotes: ''
    } as ProgramBeneficiary);
    setSearchResults([]);
    setSearchQuery('');
    setHasManualEdits(false);
    clearErrors();
  }, [clearErrors, editRegistration, setValue]);

  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (!query || query.length < 3) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const results = await searchBeneficiaryByGhanaCard(query);
        setSearchResults(results);
      } catch (error) {
        console.error('Error searching beneficiaries:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(searchQuery);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch]);

  const loadPrograms = async () => {
    try {
      const list = await programService.getPrograms();
      setPrograms(list);
    } catch (err) {
      console.error('Error loading programs', err);
    }
  };

  const cleanGhanaCardId = (idNumber = ''): string => idNumber.replace(/[-\s]/g, '').toUpperCase();

  const validateGhanaCard = (idNumber: string) => {
    if (!idNumber) return { isValid: true };
    const cleaned = cleanGhanaCardId(idNumber);
    if (cleaned.startsWith('GHA')) {
      if (!/^GHA\d{10}$/.test(cleaned)) {
        return { isValid: false, message: 'Invalid Ghana Card format (GHA##########)' };
      }
      return { isValid: true };
    }
    if (!/^\d{13}$/.test(cleaned)) {
      return { isValid: false, message: 'Invalid ID format. Use 13 digits or GHA followed by 10 digits.' };
    }
    return { isValid: true };
  };

  const handleIdNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase();
    value = value.replace(/[-\s]/g, '');
    setValue('idNumber', value, { shouldValidate: false });
    clearErrors('idNumber');
    setSearchQuery(value);
    if (selectedBeneficiary) {
      setHasManualEdits(true);
    }
  };

  const handleIdNumberBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const idNumber = e.target.value;
    if (!idNumber) return;
    const cleaned = cleanGhanaCardId(idNumber);
    const validation = validateGhanaCard(cleaned);
    if (!validation.isValid) {
      setError('idNumber', { type: 'manual', message: validation.message });
      return;
    }

    if (programId) {
      const exists = await programService.checkDuplicateId(programId, cleaned);
      if (exists && !selectedBeneficiary) {
        setError('idNumber', { type: 'manual', message: 'This Ghana Card is already registered for the selected program.' });
        ui.showMessage('This Ghana Card is already registered for the selected program.', { type: 'warning' });
      } else {
        setValue('idNumber', cleaned, { shouldValidate: true });
      }
    } else {
      setValue('idNumber', cleaned, { shouldValidate: true });
    }
  };

  const handleFieldChange = (fieldName: keyof FormData, value: any) => {
    setValue(fieldName, value);
    if (selectedBeneficiary) {
      setHasManualEdits(true);
    }
  };

  const handleSelectBeneficiary = (result: SearchResult) => {
    const { beneficiary } = result;
    
    // Store original values for comparison
    const originalData: Partial<FormData> = {
      fullName: beneficiary.fullName,
      gender: beneficiary.gender as any,
      religion: normalizeReligion((beneficiary as any).religion),
      contactNumber: beneficiary.contactNumber,
      address: beneficiary.address,
      email: beneficiary.email || '',
      idNumber: beneficiary.idNumber || '',
      maritalStatus: beneficiary.maritalStatus || '',
      healthStatus: beneficiary.healthStatus || '',
    };
    
    if ((beneficiary as any).dateOfBirth) {
      originalData.dob = formatDateForInput((beneficiary as any).dateOfBirth);
    } else if (beneficiary.age) {
      const currentYear = new Date().getFullYear();
      const birthYear = currentYear - beneficiary.age;
      originalData.dob = `${birthYear}-01-01`;
    }
    
    setOriginalValues(originalData);
    
    // Populate form fields
    setValue('fullName', beneficiary.fullName);
    setValue('gender', beneficiary.gender as any);
    setValue('religion', normalizeReligion((beneficiary as any).religion));
    setValue('contactNumber', beneficiary.contactNumber);
    setValue('address', beneficiary.address);
    setValue('email', beneficiary.email || '');
    
    if (beneficiary.idNumber) {
      setValue('idNumber', beneficiary.idNumber);
    }
    
    if (beneficiary.maritalStatus) {
      setValue('maritalStatus', beneficiary.maritalStatus);
    }
    
    if (beneficiary.healthStatus) {
      setValue('healthStatus', beneficiary.healthStatus);
    }
    
    if ((beneficiary as any).dateOfBirth) {
      setValue('dob', formatDateForInput((beneficiary as any).dateOfBirth));
    } else if (beneficiary.age) {
      const currentYear = new Date().getFullYear();
      const birthYear = currentYear - beneficiary.age;
      setValue('dob', `${birthYear}-01-01`);
    }
    
    setSelectedBeneficiary(beneficiary);
    setSearchResults([]);
    setSearchQuery('');
    setHasManualEdits(false);
    
    ui.showMessage(`Loaded details for ${beneficiary.fullName}. You can edit any field before submitting.`, { type: 'success' });
  };

  // NEW: Function to check if current form data differs from original
  const hasDataChanged = (currentData: FormData): boolean => {
    if (!selectedBeneficiary) return false;
    
    const fieldsToCompare: (keyof FormData)[] = [
      'fullName', 'gender', 'religion', 'contactNumber', 'address', 'email', 
      'idNumber', 'maritalStatus', 'healthStatus', 'dob'
    ];
    
    return fieldsToCompare.some(field => {
      const current = currentData[field] || '';
      const original = originalValues[field] || '';
      return current !== original;
    });
  };

  const onSubmit = async (data: FormData) => {
    if (!currentUser) {
      ui.showMessage('You must be logged in as admin to add members.', { type: 'error' });
      return;
    }

    if (!data.programId) {
      setError('programId', { type: 'manual', message: 'Select a program' });
      return;
    }

    if (requiresDateOfBirth && !data.dob) {
      setError('dob', { type: 'manual', message: 'Date of birth is required for this program' });
      return;
    }

    const cleanedId = data.idNumber ? cleanGhanaCardId(data.idNumber) : undefined;
    if (cleanedId) {
      const validation = validateGhanaCard(cleanedId);
      if (!validation.isValid) {
        setError('idNumber', { type: 'manual', message: validation.message });
        return;
      }
    }

    setLoading(true);
    try {
      const program = selectedProgram || programs.find(p => p.id === data.programId);

      const beneficiaryPayload: any = {
        programTitle: program?.title || '',
        fullName: data.fullName.trim(),
        gender: (data.gender || 'male') as any,
        contactNumber: data.contactNumber.trim(),
        address: data.address?.trim() || '',
        registrationStatus: 'confirmed',
        registeredBy: currentUser.uid,
        registeredByEmail: currentUser.email || null
      };

      if (data.religion) {
        beneficiaryPayload.religion = data.religion;
      }

      if (cleanedId) {
        beneficiaryPayload.idNumber = cleanedId;
        beneficiaryPayload.idType = 'ghana-card';
      }

      if (data.email?.trim()) {
        beneficiaryPayload.email = data.email.trim();
      }

      if (data.maritalStatus) {
        beneficiaryPayload.maritalStatus = data.maritalStatus;
      }

      if (data.healthStatus) {
        beneficiaryPayload.healthStatus = data.healthStatus;
      }

      if (data.dob) {
        const dob = new Date(data.dob);
        if (Number.isNaN(dob.getTime())) {
          setError('dob', { type: 'manual', message: 'Invalid date of birth' });
          setLoading(false);
          return;
        }
        // Store the actual date of birth as a Firestore Timestamp.
        // Do not set a numeric `age` here so the DB retains the full date value.
        beneficiaryPayload.dateOfBirth = Timestamp.fromDate(dob);
      }

      const normalizePhone = (p: string = '') => p.replace(/\D/g, '');
      
      // NEW: Enhanced duplicate check that returns the existing registration
      const findExistingRegistration = async (programId: string, payload: any, selected?: ProgramBeneficiary | null) => {
        try {
          const regs = await programService.getProgramRegistrations(programId, 500);
          console.debug('Checking existing members in program', programId, 'found', regs.length, 'registrations');
          const cleanedId = payload.idNumber ? payload.idNumber.toString().toUpperCase() : undefined;
          const email = payload.email ? payload.email.toLowerCase() : undefined;
          const phone = payload.contactNumber ? normalizePhone(payload.contactNumber) : undefined;
          const name = payload.fullName ? payload.fullName.trim().toLowerCase() : undefined;

          for (const r of regs) {
            if (cleanedId && r.idNumber && r.idNumber.toUpperCase() === cleanedId) {
              console.debug('Found match by idNumber', cleanedId);
              return r;
            }
            if (selected && r.beneficiaryId && selected.beneficiaryId && r.beneficiaryId === selected.beneficiaryId) {
              console.debug('Found match by beneficiaryId', selected.beneficiaryId);
              return r;
            }
            if (email && r.email && r.email.toLowerCase() === email) {
              console.debug('Found match by email', email);
              return r;
            }
            if (phone && r.contactNumber && normalizePhone(r.contactNumber) === phone && name && r.fullName && r.fullName.trim().toLowerCase() === name) {
              console.debug('Found match by phone+name', phone, name);
              return r;
            }
          }
          return null;
        } catch (err) {
          console.error('Error checking existing member in program', err);
          return null;
        }
      };

      console.log('Submitting payload:', beneficiaryPayload);

      const existingRegistration = await findExistingRegistration(data.programId, beneficiaryPayload, selectedBeneficiary);
      
      if (existingRegistration) {
        // Check if data has changed
        const dataHasChanged = hasDataChanged(data);
        
        if (!dataHasChanged) {
          if (isEditMode) {
            ui.showMessage('No changes detected to update.', { type: 'info' });
          } else {
            ui.showMessage('This member is already registered in the selected program with the same details.', { type: 'warning' });
          }
          setLoading(false);
          return;
        } else {
          // Data has changed - update the existing registration
          console.log('Updating existing registration with new details');
          
          const updateResult = await programService.updateBeneficiary(
            data.programId,
            existingRegistration.beneficiaryId,
            beneficiaryPayload
          );
          
          if (updateResult.success) {
            ui.showMessage('Member details updated successfully in this program', { type: 'success' });
            reset();
            setSelectedBeneficiary(null);
            setHasManualEdits(false);
            setOriginalValues({});
            if (isEditMode) {
              navigate('/admin/programs/registrations/add', { replace: true, state: null });
            }
          } else {
            console.error('Update result error', updateResult.error);
            ui.showMessage('Failed to update member details', { type: 'error' });
          }
          setLoading(false);
          return;
        }
      }

      if (isEditMode) {
        ui.showMessage('Could not find the original registration to update. Please reopen edit from Registrations.', { type: 'error' });
        setLoading(false);
        return;
      }

      // No existing registration - create new one
      const result = await programService.registerForProgram(data.programId, beneficiaryPayload);
      if (result.success) {
        ui.showMessage('Member added to program successfully', { type: 'success' });
        reset();
        setSelectedBeneficiary(null);
        setHasManualEdits(false);
        setOriginalValues({});
        if (isEditMode) {
          navigate('/admin/programs/registrations/add', { replace: true, state: null });
        }
      } else {
        console.error('Register result error', result.error);
        ui.showMessage('Failed to register member', { type: 'error' });
      }
    } catch (error) {
      console.error('Error registering member', error);
      ui.showMessage('Error registering member. See console for details.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full bg-white p-6 rounded-md shadow">
      <h3 className="text-lg font-semibold mb-3">{isEditMode ? 'Edit Member (Admin)' : 'Add Member (Admin)'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          
          {/* Row 1: Program Selection and Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Program *</label>
            <select {...register('programId', { required: 'Program is required' })} name="programId" className="w-full px-2 py-1 border rounded">
              <option value="">Select program</option>
              {programs.map(p => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
            {errors.programId && <p className="text-red-500 text-sm">{errors.programId.message}</p>}
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700">Search Existing Member</label>
            <div className="relative">
              <input 
                type="text" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Ghana Card or name"
                className="w-full px-2 py-1 border rounded"
              />
              {isSearching && (
                <div className="absolute right-2 top-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                </div>
              )}
              
              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {searchResults.map((result, index) => (
                    <div
                      key={index}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                      onClick={() => handleSelectBeneficiary(result)}
                    >
                      <div className="font-medium">{result.beneficiary.fullName}</div>
                      <div className="text-sm text-gray-600">
                        Ghana Card: {result.beneficiary.idNumber || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500">
                        Previously in: {result.programTitle}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">Search by Ghana Card number or name</p>
          </div>

          {/* Row 2: Ghana Card */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Ghana Card Number (optional)</label>
            <input 
              type="text" 
              {...register('idNumber')} 
              onChange={handleIdNumberChange} 
              onBlur={handleIdNumberBlur} 
              placeholder="GHA0027554249 or 13-digit" 
              className="w-full px-2 py-1 border rounded uppercase" 
            />
            {errors.idNumber && <p className="text-red-500 text-sm">{errors.idNumber.message}</p>}
            <p className="text-xs text-gray-500 mt-1">Format: GHA0027554249 or 13-digit (optional)</p>
          </div>

          {/* Row 3: Personal Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Full Name *</label>
            <input 
              type="text" 
              {...register('fullName', { 
                required: 'Full name is required', 
                minLength: { value: 2, message: 'Name must be at least 2 characters' } 
              })} 
              onChange={(e) => handleFieldChange('fullName', e.target.value)}
              className="w-full px-2 py-1 border rounded" 
            />
            {errors.fullName && <p className="text-red-500 text-sm">{errors.fullName.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Date of Birth {requiresDateOfBirth ? '*' : '(optional)'}
            </label>
            <input 
              type="date" 
              {...register('dob', requiresDateOfBirth ? { required: 'Date of birth is required for this program' } : {})} 
              onChange={(e) => handleFieldChange('dob', e.target.value)}
              className="w-full px-2 py-1 border rounded" 
            />
            {errors.dob && <p className="text-red-500 text-sm">{errors.dob.message}</p>}
          </div>

          {/* Row 4: Health & Marital Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Health Status (optional)</label>
            <select 
              {...register('healthStatus')} 
              onChange={(e) => handleFieldChange('healthStatus', e.target.value)}
              className="w-full px-2 py-1 border rounded"
            >
              <option value="">Select Status</option>
              <option value="active">Active</option>
              <option value="sick">Sick</option>
              <option value="partially_sick">Partially Sick</option>
              <option value="disabled">Disabled</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Marital Status (optional)</label>
            <select 
              {...register('maritalStatus')} 
              onChange={(e) => handleFieldChange('maritalStatus', e.target.value)}
              className="w-full px-2 py-1 border rounded"
            >
              <option value="">Select Status</option>
              <option value="single">Single</option>
              <option value="married">Married</option>
              <option value="widow">Widow</option>
              <option value="divorced">Divorced</option>
              <option value="separated">Separated</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Gender *</label>
            <select 
              {...register('gender', { required: 'Gender is required' })} 
              onChange={(e) => handleFieldChange('gender', e.target.value)}
              className="w-full px-2 py-1 border rounded"
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            {errors.gender && <p className="text-red-500 text-sm">{errors.gender.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Religion (optional)</label>
            <select
              {...register('religion')}
              onChange={(e) => handleFieldChange('religion', e.target.value)}
              className="w-full px-2 py-1 border rounded"
            >
              <option value="">Select Religion</option>
              <option value="christian">Christian</option>
              <option value="muslim">Muslim</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Row 5: Contact Information */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Number *</label>
            <input 
              type="tel" 
              {...register('contactNumber', { 
                required: 'Contact number is required', 
                pattern: { value: /^[0-9\-\+\s\(\)]{10,15}$/, message: 'Please enter a valid phone number' } 
              })} 
              onChange={(e) => handleFieldChange('contactNumber', e.target.value)}
              className="w-full px-2 py-1 border rounded" 
            />
            {errors.contactNumber && <p className="text-red-500 text-sm">{errors.contactNumber.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Address *</label>
            <input 
              type="text" 
              {...register('address', { 
                required: 'Address is required', 
                minLength: { value: 5, message: 'Please enter a valid address' } 
              })} 
              onChange={(e) => handleFieldChange('address', e.target.value)}
              className="w-full px-2 py-1 border rounded" 
            />
            {errors.address && <p className="text-red-500 text-sm">{errors.address.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email Address (optional)</label>
            <input 
              type="email" 
              {...register('email', { 
                pattern: { 
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 
                  message: 'Invalid email address' 
                } 
              })} 
              onChange={(e) => handleFieldChange('email', e.target.value)}
              className="w-full px-2 py-1 border rounded" 
            />
            {errors.email && <p className="text-red-500 text-sm">{errors.email.message}</p>}
          </div>

        </div>

        {/* Selected Beneficiary Info */}
        {selectedBeneficiary && (
          <div className={`mt-4 p-3 border rounded-md ${hasManualEdits ? 'bg-yellow-50 border-yellow-300' : 'bg-blue-50 border-blue-200'}`}>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-blue-800">
                  {hasManualEdits ? '✏️ Editing details from: ' : 'Using details from: '}
                  <span className="font-bold">{selectedBeneficiary.fullName}</span>
                </p>
                {selectedBeneficiary.idNumber && (
                  <p className="text-xs text-blue-600">
                    Original Ghana Card: {selectedBeneficiary.idNumber}
                  </p>
                )}
                {hasManualEdits && (
                  <p className="text-xs text-yellow-700 mt-1">
                    ⚠️ Modified data will update existing registration if member already exists in this program
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedBeneficiary(null);
                  setHasManualEdits(false);
                  setOriginalValues({});
                  reset();
                }}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Clear & Start New
              </button>
            </div>
          </div>
        )}

        <div className="flex items-start gap-2 mt-4">
          <input type="checkbox" {...register('agreeToTerms')} id="agreeToTermsAdmin" className="h-4 w-4 mt-1 text-primary-600 rounded" />
          <label htmlFor="agreeToTermsAdmin" className="ml-2 text-sm text-gray-700">I confirm this registrant has agreed to program terms (optional)</label>
        </div>

        <div className="flex justify-end mt-4">
          <button type="submit" disabled={loading} className="px-4 py-2 bg-primary-600 text-white rounded disabled:opacity-50">
            {loading
              ? 'Processing...'
              : isEditMode
                ? 'Update Member'
                : selectedBeneficiary
                  ? (hasManualEdits ? 'Update or Add Member' : 'Add to Program')
                  : 'Add New Member'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminProgramRegistrationForm;
